import React, { useCallback, useDeferredValue, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import {
  WorkspaceSignalListHeader,
  WorkspaceSignalOrderByKey,
} from "./WorkspaceSignalListHeader";
import { useOrgListStyleProps } from "../../useOrgHomeDetailContainerStyle";
import { WorkspaceSignalStoreList_signalWorkspace$key } from "./__generated__/WorkspaceSignalStoreList_signalWorkspace.graphql";
import { FlatList, View } from "react-native";
import { AHSeparator } from "arkh";
import { WorkspaceEmptyListItem } from "../../WorkspaceEmptyListItem";
import { WorkspaceSignalStoreListItem } from "./WorkspaceSignalStoreListItem";
import { WorkspaceSignalDescription } from "./WorkspaceSignalDescription";

export function WorkspaceSignalStoresList(props: {
  signalWorkspace: WorkspaceSignalStoreList_signalWorkspace$key;
  openSignal(signalId: string, storeName: string, storeTimezone: string): void;
  openSignalOpenedBy(
    signalId: string,
    storeName: string,
    storeTimezone: string
  ): void;
}) {
  const [search, setSearch] = React.useState("");
  const query = useDeferredValue(search);
  const [order, setOrder] = React.useState<{
    column: WorkspaceSignalOrderByKey;
    order: "ASC" | "DESC";
  }>({
    column: "store",
    order: "DESC",
  });

  const data = useFragment(
    graphql`
      fragment WorkspaceSignalStoreList_signalWorkspace on SignalWorkspace {
        signals(workspaceId: $workspaceId) {
          id
          store {
            id
            name
          }
          ...WorkspaceSignalStoreListItem_Signal
        }

        archivedAtDate {
          ...FormattedDate
        }
        ...WorkspaceSignalDescription_signalWorkspace
      }
    `,
    props.signalWorkspace
  );

  const filteredSignals = useMemo(() => {
    return query.length > 0
      ? data.signals.filter((signal) =>
          signal.store.name
            .toLocaleLowerCase()
            .includes(query.toLocaleLowerCase())
        )
      : data.signals.slice();
  }, [query, data.signals]);

  const listData = useMemo(() => {
    const sort = (
      a: (typeof filteredSignals)[number],
      b: (typeof filteredSignals)[number]
    ) => {
      switch (order.column) {
        case "store":
          return a.store.name.localeCompare(b.store.name ?? "") ?? 0;
        case "seen":
          return -1;
      }
    };
    const sortedOcccasions = filteredSignals.sort((a, b) =>
      order.order === "ASC" ? -sort(a, b) : sort(a, b)
    );
    return ["header" as const, ...sortedOcccasions] as const;
  }, [filteredSignals, order.column, order.order]);

  const listProps = useOrgListStyleProps();

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      if (item === "header") {
        return (
          <WorkspaceSignalListHeader
            order={order}
            search={search}
            setOrderBy={setOrder}
            setSearch={setSearch}
          />
        );
      }
      return (
        <WorkspaceSignalStoreListItem
          signal={item}
          isLast={index === listData.length - 1}
          openSignalOpenedBy={props.openSignalOpenedBy}
          openSignal={props.openSignal}
        />
      );
    },
    [props.openSignalOpenedBy, listData.length, order, props.openSignal, search]
  );

  const ListHeaderComponent = useMemo(() => {
    return <WorkspaceSignalDescription signalWorkspace={data} />;
  }, [data]);

  return (
    <FlatList
      {...listProps}
      ItemSeparatorComponent={AHSeparator}
      ListHeaderComponent={ListHeaderComponent}
      data={listData}
      ListFooterComponent={
        filteredSignals.length === 0 ? (
          <View>
            <AHSeparator />
            <WorkspaceEmptyListItem />
          </View>
        ) : null
      }
      stickyHeaderIndices={[1]}
      renderItem={renderItem}
    />
  );
}
